import { useEffect, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/utils/cn'
import {
  formatCostShiftValue,
  ROW_PERIODS,
  TO_PERIODS,
  type CostShiftCell,
} from './costShiftGridMockData'

interface Props {
  matrix: CostShiftCell[][]
  viewBy: string
  onCellClick?: (from: string, to: string) => void
}

const HEADER_COL_WIDTH = 84
const DATA_COL_WIDTH = 80

interface DrillMenuState {
  from: string
  to: string
  x: number
  y: number
}

export function CostShiftGridTable({ matrix, viewBy, onCellClick }: Props) {
  const [drillMenu, setDrillMenu] = useState<DrillMenuState | null>(null)

  useEffect(() => {
    if (!drillMenu) return

    const closeMenu = () => setDrillMenu(null)
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu()
    }

    window.addEventListener('pointerdown', closeMenu)
    window.addEventListener('keydown', closeOnEscape)
    window.addEventListener('scroll', closeMenu, true)

    return () => {
      window.removeEventListener('pointerdown', closeMenu)
      window.removeEventListener('keydown', closeOnEscape)
      window.removeEventListener('scroll', closeMenu, true)
    }
  }, [drillMenu])

  const openDrillMenu = (
    event: ReactMouseEvent<HTMLTableCellElement>,
    from: string,
    to: string
  ) => {
    event.preventDefault()

    const menuWidth = 240
    const menuHeight = 80
    setDrillMenu({
      from,
      to,
      x: Math.max(8, Math.min(event.clientX, window.innerWidth - menuWidth)),
      y: Math.max(8, Math.min(event.clientY, window.innerHeight - menuHeight)),
    })
  }

  const drillThrough = (from: string, to: string) => {
    setDrillMenu(null)
    onCellClick?.(from, to)
  }

  return (
    <>
      <div className="flex min-w-0 items-stretch">
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="overflow-hidden rounded-tl-lg rounded-bl-lg border border-border bg-white shadow-sm">
            <div className="max-h-[640px] overflow-auto">
              <table
                className="border-separate text-xs"
                style={{ borderSpacing: 0 }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        width: HEADER_COL_WIDTH,
                        minWidth: HEADER_COL_WIDTH,
                        left: 0,
                      }}
                      className="sticky top-0 left-0 z-30 whitespace-nowrap border-r border-b border-border bg-white px-2 py-2 text-left text-xs font-semibold text-foreground"
                    >
                      From/To
                    </th>
                    {TO_PERIODS.map(to => (
                      <th
                        key={to}
                        style={{
                          width: DATA_COL_WIDTH,
                          minWidth: DATA_COL_WIDTH,
                        }}
                        className="sticky top-0 z-20 whitespace-nowrap border-r border-b border-border bg-white px-2 py-2 text-left text-xs font-semibold text-foreground"
                      >
                        {to}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROW_PERIODS.map((from, rowIndex) => (
                    <tr key={from}>
                      <td
                        style={{
                          width: HEADER_COL_WIDTH,
                          minWidth: HEADER_COL_WIDTH,
                          left: 0,
                        }}
                        className="sticky left-0 z-10 whitespace-nowrap border-r border-b border-border bg-white px-2 py-1.5 text-left font-semibold text-foreground"
                      >
                        {from}
                      </td>
                      {matrix[rowIndex].map((cell: CostShiftCell) => {
                        const isDiagonal = cell.from === cell.to
                        const isClickable =
                          !isDiagonal && cell.value != null && !!onCellClick
                        return (
                          <td
                            key={cell.to}
                            style={{
                              width: DATA_COL_WIDTH,
                              minWidth: DATA_COL_WIDTH,
                            }}
                            className={cn(
                              'whitespace-nowrap border-r border-b border-border px-2 py-1.5 text-right tabular-nums',
                              isDiagonal && 'bg-neutral-300 font-medium',
                              isClickable && 'cursor-pointer hover:bg-blue-50'
                            )}
                            onClick={
                              isClickable
                                ? () => onCellClick(cell.from, cell.to)
                                : undefined
                            }
                            onContextMenu={
                              isClickable
                                ? event =>
                                    openDrillMenu(event, cell.from, cell.to)
                                : undefined
                            }
                            title={
                              isClickable
                                ? 'Click or right-click to drill through to PO Level Detail View'
                                : undefined
                            }
                          >
                            {cell.value != null
                              ? formatCostShiftValue(cell.value, viewBy)
                              : ''}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-center bg-emerald-300 py-2 text-sm font-semibold text-slate-900">
            Accelerations
          </div>
        </div>

        <div className="flex w-8 shrink-0 items-center justify-center bg-rose-600">
          <span className="whitespace-nowrap text-sm font-medium tracking-wide text-white [writing-mode:vertical-rl]">
            Delays
          </span>
        </div>
      </div>

      {drillMenu &&
        createPortal(
          <div
            role="menu"
            aria-label="Drill through"
            className="fixed z-[100] min-w-56 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md"
            style={{ left: drillMenu.x, top: drillMenu.y }}
            onContextMenu={event => event.preventDefault()}
            onPointerDown={event => event.stopPropagation()}
          >
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Drill through
            </div>
            <button
              type="button"
              role="menuitem"
              className="flex w-full cursor-pointer items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
              onClick={() => drillThrough(drillMenu.from, drillMenu.to)}
            >
              PO Level Detail View
            </button>
          </div>,
          document.body
        )}
    </>
  )
}
